import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Filter } from '../../models/filter-models';
import { BaseType, ClusterJewelTierValue, Tier, TierValue, UniquesTierValue } from '../../models/item-data-models';
import { imgPrefix, poe1 } from '../../../game';
import { CurrencyTierService } from '../../services/tiering/currency-tier.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { ClusterJewelTierService } from '../../services/tiering/cluster-jewel-tier.service';
import { FilterService } from '../../services/filter.service';
import { TierStyleComponent } from '../tier-style/tier-style.component';
import { DisplayType, Rarity } from '../../models/enums';
import { SelectComponent, SelectOptionComponent } from '../select/select.component';
import { ItemDataService } from '../../services/item-data.service';
import { getTierDescription } from '../helper';
import {
  isValidTierThresholdValue,
  normalizeTierThresholdValue,
  tierThresholdValueToInput,
} from '../../tier-list-tab/tier-value-helpers';

type TieringModeKind = 'currency' | 'uniques' | 'clusterJewel';
type CustomTierValue = TierValue | UniquesTierValue | ClusterJewelTierValue;
type TierLabelMode = 'currency' | 'unique' | 'plain';

interface TieringModeKindConfig {
  label: string;
  labelMode: TierLabelMode;
  poe1CurrencyOrder: boolean;
  getValues: (filter: Filter) => CustomTierValue[];
  setValues: (filter: Filter, values: CustomTierValue[]) => void;
}

const TIERING_MODE_KIND_CONFIG: Record<TieringModeKind, TieringModeKindConfig> = {
  currency: {
    label: 'Currency',
    labelMode: 'currency',
    poe1CurrencyOrder: poe1,
    getValues: (filter) => filter.tieringModeCustom,
    setValues: (filter, values) => { filter.tieringModeCustom = values as TierValue[]; },
  },
  uniques: {
    label: 'Uniques',
    labelMode: 'unique',
    poe1CurrencyOrder: poe1,
    getValues: (filter) => filter.uniquesTieringModeCustom,
    setValues: (filter, values) => { filter.uniquesTieringModeCustom = values as UniquesTierValue[]; },
  },
  clusterJewel: {
    label: 'Cluster Jewel',
    labelMode: 'plain',
    poe1CurrencyOrder: true,
    getValues: (filter) => filter.clusterJewelTieringModeCustom,
    setValues: (filter, values) => { filter.clusterJewelTieringModeCustom = values as ClusterJewelTierValue[]; },
  },
};

@Component({
  selector: 'app-custom-tiering-mode-dialog',
  imports: [FormsModule, TierStyleComponent, SelectComponent, SelectOptionComponent],
  templateUrl: './custom-tiering-mode-dialog.component.html',
  styleUrls: ['../dialogs.scss', './custom-tiering-mode-dialog.component.scss'],
})
export class CustomTieringModeDialogComponent implements OnInit {
  @Input({ required: true }) kind!: TieringModeKind;
  @Input({ required: true }) filter!: Filter;
  @Output() close = new EventEmitter<void>();

  tierValues: CustomTierValue[] = [];
  valueInputs: Record<string, string> = {};
  baseCurrencyTypes: BaseType[] = [];

  label = '';
  labelMode: TierLabelMode = 'plain';

  DisplayType = DisplayType;
  Rarity = Rarity;
  imgPrefix = imgPrefix;

  constructor(
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
    private clusterJewelTierService: ClusterJewelTierService,
    private filterService: FilterService,
    private itemDataService: ItemDataService,
  ) {}

  ngOnInit() {
    const config = TIERING_MODE_KIND_CONFIG[this.kind];
    this.label = config.label;
    this.labelMode = config.labelMode;
    this.baseCurrencyTypes = this.buildBaseCurrencyTypes(config.poe1CurrencyOrder);

    this.tierValues = config.getValues(this.filter).map(tv => ({ ...tv }));
    for (const tierValue of this.tierValues) {
      this.valueInputs[tierValue.tier] = tierThresholdValueToInput(tierValue.value);
    }
  }

  getTierCosmetic(tier: string) {
    if (this.labelMode === 'currency') {
      return this.filter.tierCosmetics.find(c => c.tier === tier);
    }
    if (this.labelMode === 'unique') {
      return this.filter.uniqueTierCosmetics.find(c => c.uniqueTier === tier);
    }
    return undefined;
  }

  getTierName(tier: string): string {
    return tier + '-Tier - ' + getTierDescription(this.filter, tier as Tier, true);
  }

  private buildBaseCurrencyTypes(poe1Order: boolean): BaseType[] {
    const find = (name: string) => this.itemDataService.getFlatItemDataByBaseTypeName(name)!.baseType;

    if (poe1Order) {
      return [find('Chaos Orb'), find('Exalted Orb'), find('Divine Orb')];
    }
    return [find('Exalted Orb'), find('Chaos Orb'), find('Divine Orb')];
  }

  onValueInputChange(tier: string, text: string) {
    this.valueInputs[tier] = text;
  }

  isValid(): boolean {
    return this.tierValues.every((tv, i) => {
      if (i === this.tierValues.length - 1) {
        return true;
      }
      return isValidTierThresholdValue(this.valueInputs[tv.tier] ?? '');
    });
  }

  save() {
    if (!this.isValid()) {
      return;
    }

    const config = TIERING_MODE_KIND_CONFIG[this.kind];
    config.setValues(this.filter, this.tierValues.map(tv => ({
      ...tv,
      value: normalizeTierThresholdValue(this.valueInputs[tv.tier])!,
    })));

    switch (this.kind) {
      case 'currency':
        this.currencyTierService.setTieringMode('Custom', this.filter);
        break;
      case 'uniques':
        this.uniquesTierService.setTieringMode('Custom', this.filter);
        break;
      case 'clusterJewel':
        this.clusterJewelTierService.setTieringMode('Custom', this.filter);
        break;
    }

    this.filterService.updateFilter(true);
    this.close.emit();
  }

  cancel() {
    this.close.emit();
  }
}
